'use client';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Auth, User } from 'firebase/auth';
import safeLocalStorage from '@/utils/safeLocalStorage';

interface AuthUserProfile {
    uid: string;
    email: string;
    name: string;
    photoURL: string;
}

interface AuthContextType {
    authUser: AuthUserProfile | null;
    uid: string | null;
    authReady: boolean;
    getIdToken: () => Promise<string | null>;
    login: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function profileFromUser(user: User): AuthUserProfile {
    return {
        uid: user.uid,
        email: user.email || '',
        name: user.displayName || '',
        photoURL: user.photoURL || '',
    };
}

function readStoredProfile(): AuthUserProfile | null {
    const stored = safeLocalStorage.getItem('authUserProfile');
    if (!stored) return null;
    try {
        const parsed = JSON.parse(stored) as Partial<AuthUserProfile>;
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            uid: parsed.uid || '',
            email: parsed.email || '',
            name: parsed.name || '',
            photoURL: parsed.photoURL || '',
        };
    } catch {
        return null;
    }
}

function hasPendingAuthRedirect(): boolean {
    try {
        if (sessionStorage.getItem('alakhbar:pendingAuthRedirect') === '1') return true;
        return Object.keys(sessionStorage).some(
            (key) =>
                key.startsWith('firebase:') &&
                (key.toLowerCase().includes('redirect') || key.toLowerCase().includes('pending'))
        );
    } catch {
        return false;
    }
}

async function loadAuthApi() {
    const [{ getFirebaseAuth, getGoogleProvider }, authMod] = await Promise.all([
        import('@/lib/firebase/firebase'),
        import('firebase/auth'),
    ]);
    const [auth, provider] = await Promise.all([getFirebaseAuth(), getGoogleProvider()]);
    return { auth, provider, authMod };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [authUser, setAuthUser] = useState<AuthUserProfile | null>(null);
    const [authReady, setAuthReady] = useState(false);
    const authRef = useRef<Auth | null>(null);
    const unsubscribeRef = useRef<(() => void) | null>(null);
    const initPromiseRef = useRef<Promise<Auth> | null>(null);
    const loginPromiseRef = useRef<Promise<void> | null>(null);

    const applyUser = useCallback((user: User | null) => {
        if (!user) {
            setAuthUser(null);
            safeLocalStorage.removeItem('authUserProfile');
            return;
        }
        const profile = profileFromUser(user);
        setAuthUser(profile);
        safeLocalStorage.setItem('authUserProfile', JSON.stringify(profile));
    }, []);

    const attachAuthListener = useCallback(
        async (auth: Auth, authMod: typeof import('firebase/auth')) => {
            if (unsubscribeRef.current) return;
            await new Promise<void>((resolve) => {
                let settled = false;
                unsubscribeRef.current = authMod.onIdTokenChanged(auth, (user) => {
                    applyUser(user);
                    setAuthReady(true);
                    if (!settled) {
                        settled = true;
                        resolve();
                    }
                });
            });
        },
        [applyUser]
    );

    const ensureFirebaseAuth = useCallback(async (): Promise<Auth> => {
        // Remount (e.g. React Strict Mode) may have cleared the listener while
        // authRef is still set — re-attach before returning early.
        if (authRef.current) {
            if (!unsubscribeRef.current) {
                const authMod = await import('firebase/auth');
                await attachAuthListener(authRef.current, authMod);
            }
            return authRef.current;
        }
        if (initPromiseRef.current) return initPromiseRef.current;

        initPromiseRef.current = (async () => {
            const { auth, authMod } = await loadAuthApi();
            authRef.current = auth;
            await attachAuthListener(auth, authMod);

            try {
                const result = await authMod.getRedirectResult(auth);
                try {
                    sessionStorage.removeItem('alakhbar:pendingAuthRedirect');
                } catch {
                    /* ignore */
                }
                if (result?.user) {
                    const idToken = await result.user.getIdToken();
                    await fetch('/engage/save-user', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${idToken}`,
                        },
                        body: JSON.stringify({
                            uid: result.user.uid,
                            email: result.user.email,
                            name: result.user.displayName,
                            photoURL: result.user.photoURL,
                        }),
                    });
                }
            } catch (error) {
                console.error('Error in getRedirectResult:', error);
            }

            return auth;
        })();

        return initPromiseRef.current;
    }, [attachAuthListener]);

    useEffect(() => {
        const stored = readStoredProfile();
        if (stored?.email || stored?.name || stored?.uid) {
            setAuthUser(stored);
        }

        const shouldRestore = Boolean(stored?.uid || stored?.email) || hasPendingAuthRedirect();
        if (!shouldRestore) {
            setAuthReady(true);
            return;
        }

        let cancelled = false;
        ensureFirebaseAuth().catch((error) => {
            if (!cancelled) {
                console.error('Firebase auth init failed:', error);
                setAuthReady(true);
            }
        });

        return () => {
            cancelled = true;
            unsubscribeRef.current?.();
            unsubscribeRef.current = null;
        };
    }, [ensureFirebaseAuth]);

    const getIdToken = async () => {
        const auth = await ensureFirebaseAuth();
        return auth.currentUser ? auth.currentUser.getIdToken() : null;
    };

    const login = async () => {
        // Firebase Auth's popup/redirect flow keeps a single in-flight "pending promise"
        // per operation. Calling signInWithPopup again (e.g. a double-clicked sign-in
        // button, or a re-render invoking login() a second time) before the first popup
        // settles overwrites that pending state and later throws
        // "INTERNAL ASSERTION FAILED: Pending promise was never set" when the first
        // popup resolves/closes. Share one in-flight promise across concurrent callers
        // instead of starting a second popup.
        if (loginPromiseRef.current) return loginPromiseRef.current;

        const promise = (async () => {
            const { auth, provider, authMod } = await loadAuthApi();
            authRef.current = auth;
            if (!unsubscribeRef.current) {
                unsubscribeRef.current = authMod.onIdTokenChanged(auth, (user) => {
                    applyUser(user);
                    setAuthReady(true);
                });
            }

            try {
                await authMod.setPersistence(auth, authMod.browserLocalPersistence);
                const result = await authMod.signInWithPopup(auth, provider);
                const idToken = await result.user.getIdToken();
                await fetch('/engage/save-user', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${idToken}`,
                    },
                    body: JSON.stringify({
                        uid: result.user.uid,
                        email: result.user.email,
                        name: result.user.displayName,
                        photoURL: result.user.photoURL,
                    }),
                });
            } catch (e: any) {
                const fallbackErrors = [
                    'auth/operation-not-supported-in-this-environment',
                    'auth/popup-blocked',
                    'auth/cancelled-popup-request',
                    'auth/popup-closed-by-user',
                ];
                if (fallbackErrors.includes(e?.code)) {
                    try {
                        sessionStorage.setItem('alakhbar:pendingAuthRedirect', '1');
                    } catch {
                        /* ignore */
                    }
                    await authMod.signInWithRedirect(auth, provider);
                    return;
                }
                console.error('Login failed', e);
                throw e;
            }
        })();

        loginPromiseRef.current = promise.finally(() => {
            loginPromiseRef.current = null;
        });

        return loginPromiseRef.current;
    };

    const logout = async () => {
        const { auth, authMod } = await loadAuthApi();
        await authMod.signOut(auth);
        safeLocalStorage.removeItem('authUserProfile');
        setAuthUser(null);
    };

    return (
        <AuthContext.Provider
            value={{
                authUser,
                uid: authUser?.uid || null,
                authReady,
                getIdToken,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
